import { Component, Input, OnInit, Output, ViewEncapsulation, EventEmitter } from '@angular/core';
import { OrganizationAdministrator, OrganizationDetail } from '../data/organization-detail';
import { VisibleAccount } from '../data/visible-account';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from '../data/data.service';
import { ModalBaseComponent } from '../add-or-edit-org-modal/modal-base.component';
import swal from 'sweetalert2/dist/sweetalert2.js';
import { Class, ClassTeacher } from '../data/class';
import { Account } from '../data/account';

@Component({
  selector: 'app-add-or-edit-user-modal',
  templateUrl: './add-or-edit-user-modal.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./add-or-edit-user-modal.css']
})
export class AddOrEditUserModal extends ModalBaseComponent<Account> implements OnInit {

  constructor(modalService: NgbModal, dataService: DataService) { super(modalService, dataService); }

  @Input() organization: OrganizationDetail;
  @Input() class: Class;
  isWaiting: boolean = false;

  selectableRolesTemplate = [
    {
      Code: 'Default',
      Description: 'Select role'
    }
  ];
  selectableRoles = this.selectableRolesTemplate;
  selectedRole = this.selectableRoles[0];
  private searchAccountResults: VisibleAccount[];
  searching = false;

  emailExists() {
    return this.searchAccountResults != null && this.searchAccountResults.find(a => a.Email === this.data.Email);
  }

  ngOnInit() {
    this.dataService.getAdministratorRoles().subscribe(
      roles => {
        roles = this.organization.TypeCode === 'CS' ? roles.filter(r => r.Code === 'TEACH') : roles.filter(r => r);
        roles = roles.sort((a, b) => (a.Description.localeCompare(b.Description)));
        this.selectableRoles = this.selectableRolesTemplate.concat(roles);
      });
  }

  compareRoles(item1, item2) {
    return item1 && item2 ? item1.Code === item2.Code : item1 === item2;
  }


  initializeNewData() {
    this.data = new Account();
    this.data.Email = '';
    this.data.Name = '';
    this.data.Password = '';
    this.data.ExternalID = '';
    this.selectedRole = this.selectableRoles[0];
  }

  isInputValid(): boolean {
    var validForNewUser = 
      this.data.Name
      && this.data.Email
      && this.data.Email.search("@") !== -1
      && this.selectedRole.Code !== 'Default';

    if (this.class) {
      return this.emailExists() ? true : validForNewUser;
    }
    else {
      return this.emailExists() ? this.selectedRole.Code !== 'Default' : validForNewUser;
    }
  }

  saveNew() {
    if (!this.emailExists()) {
      this.createAccount();
    } else {
      var verifiedAccount = this.searchAccountResults.find(a => a.Email === this.data.Email);
      if (!this.class) {
        var newAdmin = new OrganizationAdministrator();
        newAdmin.AccountID = verifiedAccount.AccountID;
        newAdmin.RoleCode = this.selectedRole.Code;
        this.addExistingAdministrator(newAdmin);
      }
      else {
        var newTeacher = new ClassTeacher();
        newTeacher.AccountID = verifiedAccount.AccountID;
        newTeacher.Email = verifiedAccount.Email;
        newTeacher.Name = verifiedAccount.AccountName;
        this.addExistingTeacher(newTeacher);
      }
    }
  }

  saveEdit(): void {
    throw new Error("Method not implemented.");
  }

  createAccount(): void {
    this.isWaiting = true;
    this.dataService.createAccount({
      Name: this.data.Name,
      Email: this.data.Email,
      Password: null,
      ExternalID: this.data.ExternalID,
      TrialDays: null,
      OrganizationRole: this.selectedRole.Code,
      OrganizationID: this.organization.ID,
      RoleCode: this.selectedRole.Code === 'TEACH' ? 'Teacher' : 'Admin',
      SendWelcomeEmail: true,
      WelcomeEmailDomain: this.dataService.getDomain(),
      ClassID: this.organization.ClassSection != null ? this.organization.ClassSection.ClassID : null
    }).subscribe(a => this.saveComplete(a));
  }

  addExistingAdministrator(newAdministrator: OrganizationAdministrator) {
    if (!newAdministrator.AccountID || !newAdministrator.RoleCode) {
      swal.fire('Oops!', 'Account and role are required.', 'error');
      return false;
    }
    if (this.organization.Administrators.some(admin => admin.AccountID === newAdministrator.AccountID)) {
      swal.fire('Oops!', 'You can not add a user more than once.', 'error');
      return false;
    }
    const newOrg = { ...this.organization, Administrators: [...this.organization.Administrators] };
    newOrg.Administrators.push(newAdministrator);
    this.dataService.updateOrganization(newOrg).subscribe(result => {
      this.saveComplete(result);
    });
  }

  addExistingTeacher(newTeacher: ClassTeacher) {
    if (!newTeacher.AccountID) {
      swal.fire('Oops!', 'Account is required.', 'error');
    }

    if (this.class.Teachers.some(teacher => teacher.AccountID === newTeacher.AccountID)) {
      swal.fire('Oops!', 'You can not add a teacher more than once.', 'error');
      return false;
    }

    const newClass = { ...this.class, Teachers: [...this.class.Teachers] };
    newClass.Teachers.push(newTeacher);
    this.dataService.updateClass(newClass).subscribe(result => {
      this.saveComplete(result);
    });
  }

  searchAccounts = (text$: Observable<string>) =>
    text$.pipe(
      tap(term => this.searching = term.length > 2),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(term => {
        if (term.length > 2) {
          return this.dataService.searchAccountsByEmail(term).pipe
            (
              tap(items => {
                this.searchAccountResults = items;
              }),
              map(items => items.filter(i => i.IsVisibileToRequestingAccount)),
              map(va => va.map(v => v.Email))
            );
        }
        else {
          return of([]);
        }
      }),
      tap(() => this.searching = false),
    )


  accountLookupSelected(selectedAccountEmail: string) {
    this.data.Email = selectedAccountEmail;
  }

  openForEdit(toEdit: Account) {
    this.modalHeader = this.organization.TypeCode === 'CS' ? "Edit User Role for Class" : "Edit User Role for Organization";
    super.openForEdit(toEdit);
  }

  openForNew() {
    this.modalHeader = this.organization.TypeCode === 'CS' ? "Add User to Class as a Teacher" : "Add User To Organization";
    super.openForNew();
  }
}

